
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Observable, firstValueFrom } from 'rxjs';
import { ISearch, ISearchResult, Payer, RestrictedAnalytesLookup, SearchLookupData } from '../models';
import { GlobalSearch } from '../models/results/global-search';
import { AppState } from '../store/reducers';
import { selectResultsState } from '../store/reducers/results.reducer';
import { ApicService } from './apic.services';
import { ConfigService } from './config.service';

@Injectable()
export class ResultsService {

  basePath?: string;
  payerPortalPath?: string;
  payerPortalSearchPath?: string;
  payerSearchQueryPath?: string;
  payerPortalResultPath?: string;
  payerSearchGlobalPath?: string;
  payerSearchResultPath?: string;
  payerSearchFTP?: string;
  restrictedAnalytesLookup: RestrictedAnalytesLookup[];

  constructor(private configService: ConfigService, private httpClient: HttpClient, private store: Store<AppState>, private apicService: ApicService) {
    this.payerPortalPath = `${this.apicService.apiPath}${this.configService.appConfig.api.baseUrl}`;
    this.payerPortalSearchPath = `${this.payerPortalPath}/search`;
    this.payerPortalResultPath = `${this.payerPortalPath}/result`;
    this.payerSearchQueryPath = `${this.apicService.apiPath}${this.configService.appConfig.api.baseSearchUrl}/query`;
    this.payerSearchGlobalPath = `${this.apicService.apiPath}${this.configService.appConfig.api.baseSearchUrl}/global`;
    this.payerSearchResultPath = `${this.apicService.apiPath}${this.configService.appConfig.api.baseSearchUrl}/result`;
    this.payerSearchFTP = `${this.apicService.apiPath}${this.configService.appConfig.api.baseSearchUrl}/ftp`;

    store.select(selectResultsState).pipe(takeUntilDestroyed()).subscribe(resultsState => {
      if (resultsState) {
        this.restrictedAnalytesLookup = resultsState.restrictedAnalytesLookup;
      }
    });
  }

  getSavedSearches(): Promise<ISearch[]> {
    return firstValueFrom(this.httpClient.get<ISearch[]>(`${this.payerSearchQueryPath}`));
  }

  getFTPSavedSearches(): Promise<ISearch[]> {
    return firstValueFrom(this.httpClient.get<ISearch[]>(`${this.payerSearchFTP}`));
  }

  getFTPSearchLookupData(orgId: string): Promise<SearchLookupData> {

    // If there is an orgId, it's a saved search and the service returns the org for that orgId.
    // If there is an orgId, the service returns the org for that orgId.
    // If there is NOT an orgId, the service returns all orgs for the user.

    if (orgId !== "") orgId = `/${orgId}`;
    const accountsPromise = firstValueFrom(this.httpClient.get<Payer[]>(`${this.payerPortalPath}/org/payerAccounts${orgId}`));
    const searchLookupPromise = firstValueFrom(this.httpClient.get<SearchLookupData>(`${this.payerSearchFTP}/lookup`));

    return Promise
      .all([accountsPromise, searchLookupPromise])
      .then(results => {
        const searchLookupData = results[1];
        searchLookupData.accounts = results[0].filter(a => a.ftpEnabled);
        return searchLookupData;
      });
  }

  getSearchLookupData(): Promise<SearchLookupData> {

    return firstValueFrom(this.httpClient.get<SearchLookupData>(`${this.payerSearchQueryPath}/lookup`)).then((searchLookupData) => {
      return firstValueFrom(this.httpClient.get<Payer[]>(`${this.payerPortalPath}/org/payerAccounts`)).then((payers) => {
        searchLookupData.accounts = payers;
        return searchLookupData;
      });
    });

  }

  getGlobalLookupData(): Promise<SearchLookupData> {
    return firstValueFrom(this.httpClient.get<SearchLookupData>(`${this.payerSearchGlobalPath}/lookup`));
  }

  getRestrictedAnalytesLookup(): Promise<RestrictedAnalytesLookup[]> {
    return firstValueFrom(this.httpClient.get<RestrictedAnalytesLookup[]>(`${this.payerPortalSearchPath}/restrictedAnalytes`));
  }

  search(searchData: ISearch): Promise<any> {
    const url = searchData.query.fileType == 'pdf' ? `${this.payerSearchQueryPath}/pdf` : `${this.payerSearchQueryPath}/adhoc`;
    return firstValueFrom(this.httpClient.post(url, searchData, { observe: 'response' })).then(response => response).catch(response => response);
  }

  globalSearch(searchData: GlobalSearch): Promise<any> {
    const url = searchData.fileType == 'pdf' ? `${this.payerSearchGlobalPath}/pdf` : `${this.payerSearchGlobalPath}/adhoc`;
    return firstValueFrom(this.httpClient.post(url, searchData, { observe: 'response' })).then(response => response)
      .catch(response => response);
  }

  preview(searchData: ISearch): Promise<any> {
    return firstValueFrom(this.httpClient
      .post(`${this.payerSearchQueryPath}/preview`, searchData, { observe: 'response' })).then(response => response).catch(response => response);
  }

  globalPreview(searchData: GlobalSearch): Promise<any> {
    return firstValueFrom(this.httpClient.post(`${this.payerSearchGlobalPath}/preview`, searchData, { observe: 'response' })).then(response => response).catch(response => response);
  }

  saveSearch(searchData: ISearch): Promise<ISearch> {
    return firstValueFrom(this.httpClient.post<ISearch>(`${this.payerSearchQueryPath}/save`, searchData)).then(response => response).catch(response => response);
  }

  deleteSearch(queryId: number): Promise<any> {
    return firstValueFrom(this.httpClient.delete(`${this.payerSearchQueryPath}/${queryId}`));
  }

  getSearchResult(encryptedResultId: string): Promise<any> {
    return firstValueFrom(this.httpClient.get(`${this.payerSearchResultPath}/${encryptedResultId}`)).then(response => response).catch(response => response);
  }

  getSavedSearch(queryId: string): Promise<ISearch> {
    return firstValueFrom(this.httpClient.get<ISearch>(`${this.payerSearchQueryPath}/${queryId}`)).then(response => response).catch(response => response);
  }

  getSearchResults(): Promise<ISearchResult[]> {
    return firstValueFrom(this.httpClient.get<ISearchResult[]>(`${this.payerSearchResultPath}`)).then(response => response).catch(response => response);
  }

  deleteSearchResult(resultId: number): Observable<any> {
    return this.httpClient.delete(`${this.payerSearchResultPath}/${resultId}`);
  }

  downloadSearchResult(resultId: number): Promise<any> {
    return firstValueFrom(this.httpClient.get(`${this.payerPortalResultPath}/getContents/${resultId}`, { responseType: 'blob' })).then(response => response)
      .catch(response => response);
  }

  getSearchBasedOnEncryptedQueryId(encryptedQueryId: string): Promise<ISearch> {
    return firstValueFrom(this.httpClient.get<ISearch>(`${this.payerSearchQueryPath}/${encryptedQueryId}`));
  }

}
